import { Component, ElementRef, ViewChild, AfterViewInit, Input, Output, EventEmitter } from '@angular/core';
import ForceGraph3D from '3d-force-graph';
import { forceManyBody, forceLink } from 'd3-force-3d';
import * as THREE from 'three';
import SpriteText from 'three-spritetext';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';

@Component({
  selector: 'app-teams-view',
  templateUrl: './teams-view.component.html',
  styleUrls: ['./teams-view.component.scss'],
})
export class TeamsViewComponent implements AfterViewInit {
  @ViewChild('graphContainer', { static: true }) graphEl!: ElementRef<HTMLDivElement>;
  @Input() fullGraphData: { nodes: any[]; links: any[] } = { nodes: [], links: [] };
  @Output() statsChanged = new EventEmitter<{
    totalProjects: number;
    domains: number;
    contributors: number;
  }>();

  graph: any;
  rawData: { nodes: any[]; links: any[] } = { nodes: [], links: [] };

  currentLevel: number = 1;
  activeTeam: string | null = null;
  activeProject: string | null = null;
  activeAlias: string | null = null;

  selectedProject: any = null;

  // dynamically adjust the ring sizes
  maxRing = 1000;

  ngAfterViewInit(): void {
    fetch('assets/teams_view_data.json')
      .then((res) => res.json())
      .then((data) => {
        this.rawData = data;
        this.initGraph();
      });

    setTimeout(() => {
      const scene = this.graph.scene?.();
      if (scene) {
        this.addStarfieldBackground(scene);
      } else {
        console.warn('Scene still undefined');
      }
    }, 500);
  }

  initGraph() {
    this.graph = new ForceGraph3D(this.graphEl.nativeElement)
      .width(this.graphEl.nativeElement.offsetWidth)
      .height(this.graphEl.nativeElement.offsetHeight)
      .graphData(this.filterLevel1Data())
      .nodeAutoColorBy('type')
      .nodeLabel((node: any) => node.id)
      .backgroundColor('#000003')
      .d3Force('charge', forceManyBody().strength(-100))
      .d3Force('link', forceLink().distance(this.maxRing / 2))
      .nodeThreeObject((node) => this.createNodeObject(node))
      .onEngineTick(() => {
        const gData = this.graph.graphData();
        gData.nodes.forEach((n: any) => {
          n.z = 0;
          n.vz = 0;
        });
      })
      .onNodeClick(this.handleNodeClick.bind(this))
      .onEngineTick(() => {
        const gData = this.graph.graphData();

        const aliasNode = this.currentLevel === 3 ? gData.nodes.find((n: any) => n.id === this.activeAlias) : null;

        if (this.currentLevel === 3 && aliasNode) {
          const centerX = aliasNode.x;
          const centerY = aliasNode.y;

          gData.nodes.forEach((node: any) => {
            if (node.type === 'project') {
              const maturity = (node.techMaturity || 'none').toLowerCase();
              const techMaturityRings: Record<'adopt' | 'trial' | 'assess' | 'identified' | 'none', number> = {
                adopt: 200,
                trial: 400,
                assess: 600,
                identified: 800,
                none: 1000,
              };

              const radius = techMaturityRings[maturity as keyof typeof techMaturityRings];

              const dx = node.x - centerX;
              const dy = node.y - centerY;
              const angle = Math.atan2(dy, dx);

              node.x = centerX + radius * Math.cos(angle);
              node.y = centerY + radius * Math.sin(angle);
              node.z = 0;
            }
          });
        }
      });

    const bloomPass = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 1.5, 1.0, 0.0);
    this.graph.postProcessingComposer().addPass(bloomPass);
  }

  private emitStats(nodes: any[]) {
    const projects = nodes.filter((n) => n.type === 'project');
    const contributorsSet = new Set<string>();
    const domainsSet = new Set<string>();

    projects.forEach((p) => {
      if (p.STIC_team)
        (Array.isArray(p.STIC_team) ? p.STIC_team : [p.STIC_team]).forEach((c: string) => contributorsSet.add(c));
      if (p.domains) (Array.isArray(p.domains) ? p.domains : [p.domains]).forEach((d: string) => domainsSet.add(d));
    });

    this.statsChanged.emit({
      totalProjects: projects.length,
      domains: domainsSet.size,
      contributors: contributorsSet.size,
    });
  }

  filterLevel1Data() {
    this.currentLevel = 1;
    this.activeTeam = null;
    this.activeProject = null;
    this.selectedProject = null;
    this.activeAlias = null;

    const nodes = this.rawData.nodes.filter((n) => n.type === 'root' || n.type === 'team');
    const nodeIds = new Set(nodes.map((n) => n.id));

    const links = this.rawData.links.filter((l) => {
      const sourceId = typeof l.source === 'string' ? l.source : l.source.id;
      const targetId = typeof l.target === 'string' ? l.target : l.target.id;
      return nodeIds.has(sourceId) && nodeIds.has(targetId);
    });

    // Calculate alias count and distance
    const teams = nodes.filter((n) => n.type === 'team');
    const center = { x: 0, y: 0, z: 0 };
    const angleStep = (2 * Math.PI) / teams.length;

    teams.forEach((n, i) => {
      // Count aliases connected to this team
      const aliasCount = this.rawData.links.filter((l) => {
        const sourceId = typeof l.source === 'string' ? l.source : l.source.id;
        const targetId = typeof l.target === 'string' ? l.target : l.target.id;
        return sourceId === n.id && this.rawData.nodes.find((nn) => nn.id === targetId && nn.type === 'alias');
      }).length;

      n.aliasCount = aliasCount;
      n.distanceFromCenter = 150 + aliasCount * 20;

      // Arrange in a circle, further out for more aliases
      const angle = i * angleStep;
      n.fx = center.x + n.distanceFromCenter * Math.cos(angle);
      n.fy = center.y + n.distanceFromCenter * Math.sin(angle);
      n.fz = 0;
    });

    // Pin the center node
    const teamsNode = nodes.find((n) => n.id === 'root');
    if (teamsNode) {
      teamsNode.fx = center.x;
      teamsNode.fy = center.y;
      teamsNode.fz = center.z;
    }

    const result = { nodes, links };
    this.emitStats(result.nodes);
    return result;
  }

  filterLevel2Data(teamId: string) {
    this.currentLevel = 2;
    this.activeTeam = teamId;
    this.activeAlias = null;

    // Show team node and its aliases
    const nodes = this.rawData.nodes.filter(
      (n) =>
        n.id === teamId ||
        (n.type === 'alias' &&
          this.rawData.links.some(
            (l) => (l.source === teamId || l.source?.id === teamId) && (l.target === n.id || l.target?.id === n.id),
          )),
    );

    const nodeIds = new Set(nodes.map((n) => n.id));

    const links = this.rawData.links.filter((l) => {
      const sourceId = typeof l.source === 'string' ? l.source : l.source.id;
      const targetId = typeof l.target === 'string' ? l.target : l.target.id;
      return nodeIds.has(sourceId) && nodeIds.has(targetId);
    });

    // Position nodes: team in center, aliases in a circle
    const teamNode = nodes.find((n) => n.id === teamId);
    const aliases = nodes.filter((n) => n.type === 'alias');

    const center = { x: 0, y: 0, z: 0 };

    if (teamNode) {
      teamNode.fx = center.x;
      teamNode.fy = center.y;
      teamNode.fz = center.z;
    }

    const angleStep = (2 * Math.PI) / Math.max(aliases.length, 1);
    const ringSpacing = 1200; // Distance from team node

    aliases.forEach((alias, i) => {
      const angle = i * angleStep;
      alias.fx = center.x + ringSpacing * Math.cos(angle);
      alias.fy = center.y + ringSpacing * Math.sin(angle);
      alias.fz = 0;
    });

    const result = { nodes, links };
    this.emitStats(result.nodes);
    return result;
  }

  filterLevel3Data(aliasId: string) {
    this.currentLevel = 3;
    this.activeAlias = aliasId;

    // Type definition for tech maturity
    type TechMaturity = 'adopt' | 'trial' | 'assess' | 'identified' | 'none';

    // Show alias node and its projects
    const nodes = this.rawData.nodes.filter(
      (n) =>
        n.id === aliasId ||
        (n.type === 'project' &&
          this.rawData.links.some(
            (l) => (l.source === aliasId || l.source?.id === aliasId) && (l.target === n.id || l.target?.id === n.id),
          )),
    );

    const links: any[] = [];

    const aliasNode = nodes.find((n) => n.id === aliasId);
    const projects = nodes.filter((n) => n.type === 'project');

    // Position alias node at center
    const center = { x: 0, y: 0, z: 0 };
    if (aliasNode) {
      aliasNode.fx = center.x;
      aliasNode.fy = center.y;
      aliasNode.fz = center.z;
    }

    // Define ring radii for tech maturity
    const techMaturityRings: Record<TechMaturity, number> = {
      adopt: 200,
      trial: 400,
      assess: 600,
      identified: 800,
      none: 1000,
    };

    // Group projects by tech maturity
    const groupedProjects: Record<TechMaturity, any[]> = {
      adopt: [],
      trial: [],
      assess: [],
      identified: [],
      none: [],
    };

    projects.forEach((p) => {
      const maturityRaw = (p.techMaturity || '').toString().trim().toLowerCase();
      const maturity: TechMaturity =
        maturityRaw === 'adopt' || maturityRaw === 'trial' || maturityRaw === 'assess' || maturityRaw === 'identified'
          ? (maturityRaw as TechMaturity)
          : 'none';

      groupedProjects[maturity].push(p);
    });

    // Position projects around the alias on rings
    (Object.keys(groupedProjects) as TechMaturity[]).forEach((maturity) => {
      const projectList = groupedProjects[maturity];
      const radius = techMaturityRings[maturity];
      const angleStep = (2 * Math.PI) / Math.max(projectList.length, 1);

      projectList.forEach((p, i) => {
        const angle = i * angleStep;
        p.fx = center.x + radius * Math.cos(angle);
        p.fy = center.y + radius * Math.sin(angle);
        p.fz = 0;
      });
    });

    const result = { nodes, links };
    this.emitStats(result.nodes);
    return result;
  }

  createNodeObject(node: any): THREE.Group {
    const group = new THREE.Group();

    let color: number | string = 0x9854ed;
    if (node.type === 'team' || node.type === 'root') color = 0x9854ed;
    else if (node.type === 'alias') color = 'white';

    if (node.type === 'root' || node.type === 'team') {
      const radius = node.type === 'root' ? 30 : 20; // bigger radius for root
      const tubeRadius = 1.5;
      const radialSegments = 16;
      const tubularSegments = 100;

      // Base torus
      const geometry = new THREE.TorusGeometry(radius, tubeRadius, radialSegments, tubularSegments);
      const material = new THREE.MeshBasicMaterial({ color: color });
      const torus = new THREE.Mesh(geometry, material);
      group.add(torus);

      // Bloom torus (glow effect)
      const bloomGeometry = new THREE.TorusGeometry(radius, tubeRadius * 1.2, radialSegments, tubularSegments);
      const bloomMaterial = new THREE.MeshBasicMaterial({ color: 0xb47cff });
      const bloomTorus = new THREE.Mesh(bloomGeometry, bloomMaterial);
      bloomTorus.layers.set(1); // bloom layer
      group.add(bloomTorus);
    } else {
      // For alias and project nodes → keep spheres
      const radius = node.type === 'project' ? 10 : 20;
      const sphere = new THREE.Mesh(new THREE.SphereGeometry(radius), new THREE.MeshBasicMaterial({ color }));
      group.add(sphere);
    }

    // Satellites for team nodes
    if (node.type === 'team' && node.aliasCount && this.currentLevel === 1) {
      const satelliteRadius = 35;
      for (let i = 0; i < node.aliasCount; i++) {
        const angle = (2 * Math.PI * i) / node.aliasCount;
        const x = satelliteRadius * Math.cos(angle);
        const y = satelliteRadius * Math.sin(angle);
        const geometry = new THREE.SphereGeometry(4, 16, 16);
        const material = new THREE.MeshBasicMaterial({ color: 0x9854ed });
        const satellite = new THREE.Mesh(geometry, material);
        satellite.position.set(x, y, 0);
        group.add(satellite);
      }
    }

    // Rings for alias nodes
    if (node.type === 'alias') {
      const labels = ['adopt', 'trial', 'assess', 'identified', 'none'];
      const ringMaterial = new THREE.MeshBasicMaterial({ color: 'gray', side: THREE.DoubleSide });

      labels.forEach((label, index) => {
        const ringRadius = 200 + index * 200; // larger spacing for alias
        const ring = new THREE.Mesh(new THREE.RingGeometry(ringRadius - 1, ringRadius, 64), ringMaterial.clone());
        ring.rotation.z = Math.PI / 2;
        ring.position.y = -1.5 * index;
        group.add(ring);

        const ringLabel = new SpriteText(label);
        ringLabel.color = 'white';
        ringLabel.textHeight = 30;
        const angle = index * 0.5;
        const x = ringRadius * Math.cos(angle);
        const y = ringRadius * Math.sin(angle);
        ringLabel.position.set(x, y, 0);
        group.add(ringLabel);
      });
    }

    // Spiral for root node
    if (node.type === 'root') {
      const texture = new THREE.TextureLoader().load('assets/spiral_teams.png');
      const material = new THREE.SpriteMaterial({ map: texture, transparent: true, opacity: 0.15 });
      const spiral = new THREE.Sprite(material);
      spiral.scale.set(165, 165, 1);
      spiral.position.set(0, 0, 0);
      group.add(spiral);
    }

    const label = new SpriteText(node.id);
    label.color = 'white';
    if (node.type === 'project') {
      label.textHeight = 10;
      label.position.set(0, 15, 0);
    } else if (node.type === 'root') {
      label.textHeight = 30;
      label.position.set(0, 45, 0);
    } else {
      label.textHeight = 20;
      label.position.set(0, 30, 0);
    }
    group.add(label);

    return group;
  }

  handleNodeClick(node: any) {
    if (this.currentLevel === 1 && node.type === 'team') {
      this.graph.graphData(this.filterLevel2Data(node.id));
    } else if (this.currentLevel === 2 && node.type === 'alias') {
      this.graph.graphData(this.filterLevel3Data(node.id));
    } else if (this.currentLevel === 3 && node.type === 'project') {
      console.log('Project node clicked:', node);

      this.selectedProject = {
        id: node.id,
        domain: node.domains ? 'Domain: ' + node.domains : 'Domain: Unknown',
        techMaturity: node.techMaturity?.length > 0 ? 'Tech Maturity: ' + node.techMaturity : 'Tech Maturity: Unknown',
        team: Array.isArray(node.team)
          ? node.team.length > 0
            ? 'Team: ' + node.team.join(', ')
            : 'Team: Unknown'
          : node.team?.length > 0
            ? 'Team: ' + node.team
            : 'Team: Unknown',
        STICTeam: node.STIC_team?.length > 0 ? 'Team Members: ' + node.STIC_team.join(', ') : 'Team Members: Unknown',
        stakeholders: node.stakeholders?.length > 0 ? 'Stakeholders: ' + node.stakeholders : 'Stakeholders: Unknown',
        mainConcept:
          node.mainConcepts?.length > 0 ? 'Main Technologies: ' + node.mainConcepts : 'Main Technologies: Unknown',
        subConcept:
          node.subConcepts?.length > 0 ? 'Sub Technologies: ' + node.subConcepts : 'Sub Technologies: Unknown',
        description: node.markdown ? 'Description: ' + node.markdown : 'No description available.',
        path: node.path || 'Unknown Path',
      };
    } else if (node.type === 'root') {
      this.graph.graphData(this.filterLevel1Data());
    }
  }

  closeInfoBox() {
    this.selectedProject = null;
  }

  resetGraph() {
    this.graph.graphData(this.filterLevel1Data());
    this.selectedProject = null;
  }

  centerGraph() {
    this.graph.zoomToFit(500);
  }

  addStarfieldBackground(scene: THREE.Scene) {
    const starsGeometry = new THREE.BufferGeometry();
    const starCount = 20000;

    const positions = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount * 3; i++) {
      positions[i] = (Math.random() - 0.5) * 20000; // make this bigger if needed for width/height
    }

    starsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const starsMaterial = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 2,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.7,
    });

    const stars = new THREE.Points(starsGeometry, starsMaterial);
    scene.add(stars);
  }
}
